import { CommandType, DistanceType, TurnResult } from '../types';

type CommandModifiers = Record<CommandType, number>;

const NEUTRAL_MODIFIERS: CommandModifiers = {
  [CommandType.ADVANCE]: 1.0,
  [CommandType.RETREAT]: 1.0,
  [CommandType.WEAPON_ATTACK]: 1.0,
  [CommandType.SPECIAL_ATTACK]: 1.0,
  [CommandType.REFLECTOR]: 1.0,
  [CommandType.STANCE_A]: 1.0,
  [CommandType.STANCE_B]: 1.0,
};

/**
 * 成功パターン
 */
export interface SuccessPattern {
  /** 成功したコマンドの種類 */
  command: CommandType;
  /** 成功時の距離 */
  distance: DistanceType;
  /** 与えたダメージ */
  damageDealt: number;
  /** ターン番号 */
  turnNumber: number;
}

/**
 * 成功パターンの抽出
 *
 * 直近のターン履歴から、自分がダメージを与えたコマンドを
 * 成功パターンとして抽出する。
 *
 * @param turnHistory ターン履歴
 * @param selfPlayerId 自分のプレイヤーID
 * @param lookback 何ターン遡るか（デフォルト: 5）
 * @returns 成功パターンの配列
 */
export function extractSuccessPatterns(
  turnHistory: TurnResult[],
  selfPlayerId: 'player1' | 'player2',
  lookback: number = 5
): SuccessPattern[] {
  if (turnHistory.length === 0 || lookback <= 0) {
    return [];
  }

  const startIndex = Math.max(0, turnHistory.length - lookback);
  const patterns: SuccessPattern[] = [];

  for (let i = startIndex; i < turnHistory.length; i++) {
    const turn = turnHistory[i];

    // ターン開始時の距離を推定（前ターンのdistanceAfter、なければ1stフェーズの状態を参考）
    const turnStartDistance = i > 0
      ? turnHistory[i - 1].distanceAfter
      : turn.phases[0].distanceAfter;

    // フェーズごとにダメージを判定して成功パターンを記録
    let hasAttackInTurn = false;
    for (const [phaseIndex, phase] of turn.phases.entries()) {
      if (phase.isSkipped) continue;

      const myCmd = selfPlayerId === 'player1' ? phase.player1Command : phase.player2Command;
      const phaseDamage = selfPlayerId === 'player1'
        ? phase.player2Damage.damage
        : phase.player1Damage.damage;

      const isAttack = myCmd === CommandType.WEAPON_ATTACK || myCmd === CommandType.SPECIAL_ATTACK;
      if (isAttack) {
        hasAttackInTurn = true;
        if (phaseDamage > 0) {
          const phaseDistance = phaseIndex === 0 ? turnStartDistance : turn.phases[0].distanceAfter;
          patterns.push({
            command: myCmd,
            distance: phaseDistance,
            damageDealt: phaseDamage,
            turnNumber: turn.turnNumber,
          });
        }
      }
    }

    // 攻撃コマンドがない場合でもダメージがある（リフレクター反射等）場合、
    // 移動・防御コマンドも間接的な成功として記録
    if (!hasAttackInTurn) {
      const totalDamage = selfPlayerId === 'player1'
        ? turn.player2Damage.damage
        : turn.player1Damage.damage;
      if (totalDamage > 0) {
        const myCommands = selfPlayerId === 'player1' ? turn.player1Commands : turn.player2Commands;
        for (const cmd of [myCommands.first.type, myCommands.second.type]) {
          patterns.push({
            command: cmd,
            distance: turnStartDistance,
            damageDealt: totalDamage,
            turnNumber: turn.turnNumber,
          });
        }
      }
    }
  }

  return patterns;
}

/**
 * 成功パターン学習モディファイア
 *
 * 直近のターン履歴から成功パターンを抽出し、成功したコマンドの重みを上げる。
 * 現在の距離と成功時の距離が一致する場合、追加ボーナスを付与する。
 *
 * @param turnHistory ターン履歴
 * @param selfPlayerId 自分のプレイヤーID
 * @param currentDistance 現在の距離
 * @param lookback 何ターン遡るか（デフォルト: 5）
 * @returns コマンドごとの重みモディファイア
 */
export function getSuccessPatternModifiers(
  turnHistory: TurnResult[],
  selfPlayerId: 'player1' | 'player2',
  currentDistance: DistanceType,
  lookback: number = 5
): CommandModifiers {
  const patterns = extractSuccessPatterns(turnHistory, selfPlayerId, lookback);

  if (patterns.length === 0) {
    return { ...NEUTRAL_MODIFIERS };
  }

  // コマンドごとの成功スコアを集計
  const commandScores: Partial<Record<CommandType, number>> = {};

  for (const pattern of patterns) {
    const distanceBonus = pattern.distance === currentDistance ? 1.5 : 1.0;
    const score = (pattern.damageDealt / 100) * distanceBonus;
    commandScores[pattern.command] = (commandScores[pattern.command] ?? 0) + score;
  }

  // スコアをモディファイアに変換（緩やかに影響）
  const SCALE = 0.15;
  const MIN_MODIFIER = 0.5;
  const MAX_MODIFIER = 2.0;

  const result: CommandModifiers = { ...NEUTRAL_MODIFIERS };
  for (const cmd of Object.values(CommandType)) {
    const score = commandScores[cmd] ?? 0;
    if (score > 0) {
      result[cmd] = Math.max(MIN_MODIFIER, Math.min(MAX_MODIFIER, 1.0 + SCALE * score));
    }
  }

  return result;
}
